import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';
import { TicketCategory, TicketPriority } from '@bumble/app/__generated__/data-contracts';
import { TicketFileInput } from '@bumble/app/__generated__/Api';
import { TicketAttachmentUploader } from './TicketAttachmentUploader';

interface CreateCaseProps {
  onSubmit: () => void;
}

interface DeviceName {
  name: string;
  id: string;
}
interface DeviceNames {
  cpe: DeviceName[];
  metro: DeviceName[];
}

export const CreateCase = ({ onSubmit }: CreateCaseProps) => {
  const api = useApi();

  const [subject, setSubject] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [priorities, setPriorities] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<TicketCategory>();
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [deviceOptions, setDeviceOptions] = useState<{ id: string; label: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<{ id: string; label: string } | null>(null);
  const [attachments, setAttachments] = useState<TicketFileInput[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const createEntity = useMutation(api.createTicketApiV1CreateTicketPost, {
    onSuccess() {
      toast.success(`Created Case ${subject} successfully.`);
      onSubmit();
    },
    onError: apiError,
  });

  const { isLoading: isLoadingPriorities, data: ticketPriorities } = useQuery(
    ['ticketPriorities'],
    api.getTicketPriorityApiV1GetTicketPriorityGet.bind(null),
    { enabled: true, cacheTime: 0, initialData: undefined }
  );

  const { isLoading: isLoadingCategories, data: ticketCategories } = useQuery(
    ['ticketCategories'],
    api.getTicketCategoryApiV1GetTicketCategoryGet.bind(null),
    { enabled: true, cacheTime: 0, initialData: undefined }
  );

  const { data: deviceNamesData } = useQuery(
    ['dashboarddevicenames'],
    api.dashboardGetDeviceNamesApiV1DashboardGetDeviceNamesGet.bind(null, {}),
    { enabled: true }
  );

  useEffect(() => {
    const tmp = deviceNamesData?.data as DeviceNames | undefined;
    const built =
      (tmp
        ? ([] as DeviceName[]).concat(tmp.cpe || [], tmp.metro || [])
        : []
      ).map((n) => ({ label: n.name, id: n.id }));

    const withNone = [{ id: 'none', label: 'None' }, ...built];
    setDeviceOptions(withNone);
    setSelectedDevice((prev) => prev ?? withNone[0]);
  }, [deviceNamesData]);

  useEffect(() => {
    if (ticketPriorities !== null && ticketPriorities !== undefined) {
      setPriorities(ticketPriorities.data || []);
    }
  }, [ticketPriorities]);

  useEffect(() => {
    if (ticketCategories !== null && ticketCategories !== undefined) {
      setCategories((ticketCategories.data as TicketCategory[]) || []);
    }
  }, [ticketCategories]);

  useEffect(() => {
    if (priorities.length > 0 && !selectedPriority) {
      setSelectedPriority(priorities[0]);
    }
  }, [priorities, selectedPriority]);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  const handleSubmit = () => {
    if (!selectedPriority || !selectedCategory) {
      toast.error('Please select a priority and category.');
      return;
    }
    createEntity.mutate({
      title: subject,
      description: description || '',
      priority: selectedPriority as TicketPriority,
      ...(selectedDevice?.id !== 'none' && { device_id: selectedDevice?.id }),
      category: selectedCategory as TicketCategory,
      uploaded_files: attachments,
    });
  };

  const handlePriorityChange = ({ target: { value } }: SelectChangeEvent) => {
    setSelectedPriority(value);
  };

  const handleCategoryChange = ({ target: { value } }: SelectChangeEvent) => {
    setSelectedCategory(value as TicketCategory);
  };

  if (isLoadingPriorities || isLoadingCategories) return <Loading />;

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography fontSize="h6" fontWeight="bold" mb={2}>
            Subject
          </Typography>
          <TextField
            fullWidth
            id="subject"
            variant="outlined"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus
          />
          <Typography fontSize="0.8em">
            Maximum 250 characters (250 - {subject.length} remaining)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography fontSize="h6" fontWeight="bold" mb={2}>
            Description
          </Typography>
          <Typography fontSize="0.8em">
            Don't share any sensitive information in case correspondences, such as credentials, credit cards, signed
            URL's, or personally identifiable information.
          </Typography>
          <TextField
            fullWidth
            multiline
            id="description"
            minRows={4}
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Typography fontSize="0.8em">
            Maximum 8000 characters (8000 - {description.length} remaining)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="priority-label">Priority</InputLabel>
            <Select
              labelId="priority-label"
              label="Priority"
              value={selectedPriority || ''}
              onChange={handlePriorityChange}
            >
              {priorities.map((item) => (
                <MenuItem value={item} key={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              label="Category"
              value={selectedCategory ?? ''}
              onChange={handleCategoryChange}
            >
              {categories.map((item) => (
                <MenuItem value={item} key={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="device-label">Device</InputLabel>
            <Select
              labelId="device-label"
              value={selectedDevice?.id || ''}
              label="Device"
              onChange={(e) => {
                const selected = deviceOptions.find((opt) => opt.id === e.target.value);
                setSelectedDevice(selected ?? null);
              }}
            >
              {deviceOptions.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography fontSize="h6" fontWeight="bold" mb={2}>
            Attachments
          </Typography>
          <TicketAttachmentUploader
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            onUploadingChange={setIsUploadingAttachments}
            disabled={createEntity.isLoading}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            subject === '' ||
            !selectedPriority ||
            !selectedCategory ||
            isUploadingAttachments ||
            createEntity.isLoading
          }
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};
